import gql from 'graphql-tag';
import { useQuery } from '@vue/apollo-composable';
import { ref, computed } from 'vue';

export enum UserType {
    OWNER = 0,
    READONLY = 1,
    ADMIN = 2,
    DEMO = 3
}

export type UserRole = 'owner' | 'readonly' | 'admin' | 'demo';
export type Profile = {
    domain: string;
    name: string;
    ownerEmail: string | null;
    sid: string;
    siteid?: string;
    status: 1;
    type: UserType;
    timeZone: 'string';
    pteEnabled: 0 | 1;
    ptxEnabled: 0 | 1;
    lpEnabled: 0 | 1;
    isActived: 0 | 1;
    profileSource: 'ptengine' | 'ptx';

    ptiUserRole: UserRole;
    ptxUserRole: UserRole;
    lpUserRole?: UserRole;

    engageCount: number;
    engageRunningCount: number;
    goalCount: number;
    ptengineCrossDomainSetting: number;
    createTime: number | null;
    updateTime: number | null;
    activeTime: number | null;
    usergroupCount: number;
    sevenPV: number;
    sevenActiveUsers: number;

    isNpPackage: boolean | null;

    packageType?: string;

    additionalId?: string;
};

export type ProfileCurrentNpPackageInfoType = {
    profileId: string;
    profileName: string;
    profileOwnerEmail: string;
    ptxPackageId: string;
    ptiPackageId: string;
    monthlyPvUsage: number;
    isRecurringPaymentStatus: Boolean;
    ptxRole: string;
    ptiRole: string;
    packageType: string;
    bindWay: number;
};

const GET_ALL_PROFILE_PACKAGE_TYPE_QUERY = gql`
    query getAllProfilePackageType($profileIds: [String!]!) {
        getAllProfilePackageType(profileIds: $profileIds) {
            profileId
            packageType
        }
    }
`;

const PROFILES_QUERY = gql`
    query newPtengineProfiles {
        newPtengineProfiles {
            ownerEmail
            sid
            siteid
            type
            name
            domain
            timeZone
            createTime
            updateTime
            isActived
            activeTime
            profileSource
            status
            engageCount
            engageRunningCount
            usergroupCount
            goalCount
            currentUserRole
            ptxUserRole
            ptiUserRole
            pteEnabled
            ptxEnabled
            lpEnabled
            ptengineCrossDomainSetting
            sevenPV
            sevenActiveUsers
            consumedProfile
            primaryDomain
            campaignStatus
            eventStatus
            isNpPackage
            additionalId
        }
    }
`;

const queryProfileListLoading = ref(false);
const allProfile = ref<Array<Profile> | undefined>();
const profileListPackageInfo = ref<Array<ProfileCurrentNpPackageInfoType>>();

export function useProfileList() {
    /**
     * 获取档案列表
     */
    const getProfileList = (
        resultCallback?: (T: Array<Profile>) => void,
        rejectCallback?: (err: any) => void
    ) => {
        queryProfileListLoading.value = true;

        const { onResult, onError } = useQuery<{ newPtengineProfiles: Array<Profile> }>(
            PROFILES_QUERY,
            {},
            {
                fetchPolicy: 'no-cache'
            }
        );

        onResult(data => {
            if (data.data.newPtengineProfiles) {
                allProfile.value = data.data.newPtengineProfiles;

                // 执行回调
                resultCallback &&
                    typeof resultCallback === 'function' &&
                    resultCallback(allProfile.value);
            }
            queryProfileListLoading.value = false;
        });

        onError(err => {
            allProfile.value = undefined;
            queryProfileListLoading.value = false;
            rejectCallback && typeof rejectCallback === 'function' && rejectCallback(err);
        });
    };

    /**
     * 获取所有档案的套餐类型
     */
    const getProfileListPackageInfo = (
        resultCallback?: (T: Array<ProfileCurrentNpPackageInfoType>) => void,
        rejectCallback?: (err: any) => void
    ) => {
        if (!allProfile.value) return;

        const profileIds = allProfile.value?.map(profile => profile.sid);
        // 空档案时执行成功回调
        if (profileIds?.length === 0) {
            profileListPackageInfo.value = [];
            resultCallback &&
                typeof resultCallback === 'function' &&
                resultCallback(profileListPackageInfo.value);
            return;
        }

        const { onResult, onError } = useQuery<{
            getAllProfilePackageType: Array<ProfileCurrentNpPackageInfoType>;
        }>(
            GET_ALL_PROFILE_PACKAGE_TYPE_QUERY,
            {
                profileIds
            },
            {
                fetchPolicy: 'no-cache'
            }
        );

        onResult(data => {
            if (data.data.getAllProfilePackageType) {
                profileListPackageInfo.value = data.data.getAllProfilePackageType;

                // 执行回调
                resultCallback &&
                    typeof resultCallback === 'function' &&
                    resultCallback(profileListPackageInfo.value);
            }
        });

        onError(err => {
            profileListPackageInfo.value = undefined;
            rejectCallback && typeof rejectCallback === 'function' && rejectCallback(err);
        });
    };

    /**
     * 档案列表添加套餐信息
     */
    const profileList = computed(() => {
        if (!allProfile.value || !profileListPackageInfo.value) return undefined;

        return allProfile.value
            ?.map(profile => ({
                ...profile,
                ptxEnabled: profile.isNpPackage === true ? 1 : profile.ptxEnabled,
                packageType: profileListPackageInfo.value?.find(
                    (_profile: any) => _profile.profileId === profile.sid
                )?.packageType
            }))
            .sort((a, b) => b.sevenPV - a.sevenPV);
    });

    /**
     * 非Demo的列表
     */
    const profileListWithoutDemo = computed(() =>
        profileList.value?.filter(profile => profile.type !== 3)
    );

    return {
        profileList,
        profileListPackageInfo,
        profileListWithoutDemo,
        queryProfileListLoading,

        getProfileList,
        getProfileListPackageInfo
    };
}
